import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { Box, Typography, TextField, Button, Stack, Alert, Divider, IconButton, InputAdornment, List, ListItem, ListItemText, ListItemSecondaryAction } from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import CloseIcon from '@mui/icons-material/Close'
import ClearIcon from '@mui/icons-material/Clear'
import EditIcon from '@mui/icons-material/Edit'
import AddIcon from '@mui/icons-material/Add'
import SelectAllIcon from '@mui/icons-material/SelectAll'
import DeselectIcon from '@mui/icons-material/Deselect'
import DeleteIcon from '@mui/icons-material/Delete'
import useAccount from '../hooks/useAccount'
import useRouter from '../hooks/useRouter'
import useSnackbar from '../hooks/useSnackbar'
import useVariableTree from '../hooks/useVariableTree'
import useBatchGenerate from '../hooks/useBatchGenerate'
import Tree from '../components/tree/Tree'
import AxisEditor from '../components/spec-editor/AxisEditor'
import FilterEditorModal from '../components/spec-editor/FilterEditorModal'
import BatchConfigPanel from '../components/batch/BatchConfigPanel'
import BatchConfirmDialog, { BatchTablePreview } from '../components/batch/BatchConfirmDialog'
import FolderPickerDialog from '../components/batch/FolderPickerDialog'
import SideBarPage from '../components/system/SidebarPage'
import PageHeader from '../components/system/PageHeader'
import ActionButton from '../components/system/ActionButton'
import { GenNode } from '../types/carbon/models/GenNode'
import { getNodeID } from '../utils/nodeTypes'

const BatchGenerate: React.FC = () => {
  const account = useAccount()
  const router = useRouter()
  const snackbar = useSnackbar()

  const customer = router.params.customer || account.customer?.name || ''
  const job = router.params.job || account.job?.name || ''

  const [confirmOpen, setConfirmOpen] = useState(false)
  const [folderPickerOpen, setFolderPickerOpen] = useState(false)
  const [tablePreviews, setTablePreviews] = useState<BatchTablePreview[]>([])

  // Side variables — managed separately from tree checkboxes
  const [sideVarNames, setSideVarNames] = useState<string[]>([])

  const batch = useBatchGenerate({ customer, job })

  // Load TOC if not already loaded (e.g. direct navigation to this page)
  useEffect(() => {
    if (customer && job && account.toc.length === 0) {
      account.reloadTOC()
    }
  }, [customer, job])

  // Variable tree with loadCodes=false so we only show folders and variables
  const variableTree = useVariableTree({
    customer,
    job,
    loadCodes: false,
  })

  // Extract selected variable GenNode objects from tree checkboxes (for AxisEditor "+" button)
  const selectedVarNodes = useMemo(() => {
    if (!variableTree.nodes || variableTree.checked.length === 0) return []
    const nodes: GenNode[] = []
    const collect = (items: GenNode[]) => {
      for (const item of items) {
        if (item.type === 'Variable' && variableTree.checked.includes(getNodeID(item))) {
          nodes.push(item)
        }
        if (item.children) collect(item.children)
      }
    }
    collect(variableTree.nodes)
    return nodes
  }, [variableTree.nodes, variableTree.checked])

  // Extract selected variable names from tree checkboxes
  const selectedVarNamesList = useMemo(() => {
    return selectedVarNodes.map(n => n.value1 || '').filter(Boolean)
  }, [selectedVarNodes])

  // Custom checkbox state that enables folders and variables
  const getCheckboxState = useCallback((node: GenNode) => {
    if (node.type === 'Folder') {
      const descendantVarIds: string[] = []
      const collectVarIds = (n: GenNode) => {
        if (n.type === 'Variable') descendantVarIds.push(getNodeID(n))
        if (n.children) n.children.forEach(collectVarIds)
      }
      if (node.children) node.children.forEach(collectVarIds)
      const allChecked = descendantVarIds.length > 0 && descendantVarIds.every(id => variableTree.checked.includes(id))
      return { enabled: true, checked: allChecked }
    }
    if (node.type === 'Variable') {
      return { enabled: true, checked: variableTree.checked.includes(getNodeID(node)) }
    }
    return { enabled: false, checked: false }
  }, [variableTree.checked])

  // Custom check toggle that handles folder selection
  const onNodeCheckedToggle = useCallback((_itemId: string, node: GenNode, isChecked: boolean) => {
    if (node.type === 'Folder') {
      const varIds: string[] = []
      const collectVarIds = (n: GenNode) => {
        if (n.type === 'Variable') varIds.push(getNodeID(n))
        if (n.children) n.children.forEach(collectVarIds)
      }
      if (node.children) node.children.forEach(collectVarIds)
      variableTree.setChecked(prev => {
        if (isChecked) {
          return [...new Set([...prev, ...varIds])]
        } else {
          return prev.filter(id => !varIds.includes(id))
        }
      })
    } else if (node.type === 'Variable') {
      const nodeId = getNodeID(node)
      variableTree.setChecked(prev => {
        if (isChecked) {
          return [...prev, nodeId]
        } else {
          return prev.filter(id => id !== nodeId)
        }
      })
    }
  }, [variableTree])

  // Add checked variables from tree to side variables list (the "+" button action)
  const handleAddToSideVariables = useCallback(() => {
    if (selectedVarNamesList.length === 0) return
    setSideVarNames(prev => {
      const newNames = selectedVarNamesList.filter(name => !prev.includes(name))
      return [...prev, ...newNames]
    })
    variableTree.resetChecked()
  }, [selectedVarNamesList, variableTree])

  const handleSelectAll = () => {
    if (!variableTree.nodes) return
    const allVarIds: string[] = []
    const collect = (nodes: GenNode[]) => {
      for (const node of nodes) {
        if (node.type === 'Variable') allVarIds.push(getNodeID(node))
        if (node.children) collect(node.children)
      }
    }
    collect(variableTree.nodes)
    variableTree.setChecked(allVarIds)
  }

  const handleClearAll = () => {
    variableTree.resetChecked()
  }

  const handleReviewGenerate = async () => {
    if (sideVarNames.length === 0) {
      snackbar.error('Select at least one variable')
      return
    }
    if (!batch.topSyntax.trim()) {
      snackbar.error('Top axis is required')
      return
    }

    snackbar.info('Loading variable descriptions...')
    const descs = await batch.fetchVariableDescriptions(sideVarNames)
    const previews = batch.buildTablePreviews(sideVarNames, descs)
    setTablePreviews(previews)
    setConfirmOpen(true)
  }

  const handleConfirmGenerate = async () => {
    try {
      const descs = batch.descriptions
      const result = await batch.submit(sideVarNames, descs)
      snackbar.info(`Generated ${result.count} table${result.count !== 1 ? 's' : ''}`)
      setConfirmOpen(false)
      account.openPage('tables')
      account.reloadTOC()
    } catch (err) {
      snackbar.error(`Batch generation failed: ${(err as Error).message}`)
    }
  }

  const handleCancel = () => {
    account.openPage('tables')
  }

  const handleFolderSelect = (folderPath: string, dest: 'user' | 'exec') => {
    batch.setDestinationFolder(folderPath)
    batch.setDestination(dest)
  }

  const handleRemoveSideVariable = (name: string) => {
    setSideVarNames(prev => prev.filter(n => n !== name))
  }

  const isManager = account.user?.session_info?.roles?.some(
    r => r.toLowerCase() === 'manager'
  ) || false

  const displayDestination = batch.destination === 'exec'
    ? `Exec${batch.destinationFolder ? '/' + batch.destinationFolder : ''}`
    : `My Tables${batch.destinationFolder ? '/' + batch.destinationFolder : ''}`

  return (
    <SideBarPage
      title="Variables"
      headerContent={
        <Stack direction="row" spacing={0.5} sx={{ ml: 1 }}>
          <ActionButton icon={<SelectAllIcon />} label="Select All" onClick={handleSelectAll} />
          <ActionButton icon={<DeselectIcon />} label="Clear All" onClick={handleClearAll} />
        </Stack>
      }
      sidebar={
        variableTree.isLoading ? (
          <Typography color="text.secondary" sx={{ p: 2 }}>Loading variables...</Typography>
        ) : variableTree.isError ? (
          <Alert severity="error" sx={{ m: 1 }}>Failed to load variable tree</Alert>
        ) : (
          <Tree
            nodes={variableTree.nodes || []}
            expanded={variableTree.expanded}
            onNodeExpandToggle={variableTree.onNodeExpandToggle}
            getCheckboxState={getCheckboxState}
            onNodeCheckedToggle={onNodeCheckedToggle}
            searchEnabled
          />
        )
      }
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <PageHeader
          title="Batch Generate Tables"
          subtitle={`${sideVarNames.length} table${sideVarNames.length !== 1 ? 's' : ''} to create`}
          actions={
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" color="inherit" onClick={handleCancel} startIcon={<CloseIcon />}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="info"
                onClick={handleReviewGenerate}
                startIcon={<PlayArrowIcon />}
                disabled={sideVarNames.length === 0 || !batch.topSyntax.trim()}
              >
                Review & Generate
              </Button>
            </Stack>
          }
        />

        {/* Batch Settings — single row */}
        <BatchConfigPanel
          prefix={batch.prefix}
          suffix={batch.suffix}
          destinationFolder={displayDestination}
          onPrefixChange={batch.setPrefix}
          onSuffixChange={batch.setSuffix}
          onPickFolder={() => setFolderPickerOpen(true)}
        />

        {/* Filter / Weight / Case Filter bar */}
        <Box sx={{
          display: 'flex',
          gap: 1.5,
          alignItems: 'center',
          px: 3,
          pt: 2.5,
          pb: 1.5,
          backgroundColor: 'background.default',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}>
          <Typography variant="subtitle2" sx={{
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            fontSize: '0.75rem',
            mr: 0.5,
          }}>
            Filter and Weighting
          </Typography>

          <Divider orientation="vertical" flexItem sx={{ my: '4px' }} />

          <TextField
            value={batch.filter || ''}
            label="Filter"
            placeholder="Filter expression"
            size="small"
            sx={{ flex: 1, maxWidth: 320 }}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end" sx={{ gap: 0 }}>
                  {batch.filter && (
                    <IconButton size="small" onClick={() => batch.setFilter('')} sx={{ p: 0.25 }}>
                      <ClearIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                  <IconButton size="small" onClick={() => batch.handleOpenFilterModal()} sx={{ p: 0.25 }}>
                    <EditIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Divider orientation="vertical" flexItem sx={{ my: '4px' }} />

          <TextField
            value={batch.weight || ''}
            onChange={(e) => batch.setWeight(e.target.value)}
            label="Weight"
            placeholder="WeightCombo()"
            size="small"
            sx={{ width: 180 }}
            InputProps={{
              endAdornment: batch.weight ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => batch.setWeight('')} sx={{ p: 0.25 }}>
                    <ClearIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />

          <Divider orientation="vertical" flexItem sx={{ my: '4px' }} />
          <TextField
            value={batch.caseFilter || ''}
            onChange={(e) => batch.setCaseFilter(e.target.value)}
            label="Case Filter"
            placeholder="BaseCategoryFilter"
            size="small"
            sx={{ width: 180 }}
            InputProps={{
              endAdornment: batch.caseFilter ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => batch.setCaseFilter('')} sx={{ p: 0.25 }}>
                    <ClearIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
        </Box>

        {/* Two-panel area: Side Variables | Top Axis Editor */}
        <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
          {/* Left panel: Side Variables */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: 'background.paper' }}>
            <Box sx={{ px: 4, pt: 4, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton
                  onClick={handleAddToSideVariables}
                  size="small"
                  sx={{
                    backgroundColor: '#009900',
                    color: '#ffffff',
                    width: 32,
                    height: 32,
                    '&:hover': { backgroundColor: '#007700' },
                  }}
                  disabled={selectedVarNamesList.length === 0}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
                <Typography variant="h6" component="h3" sx={{ flex: 1 }}>
                  Tables to Create ({sideVarNames.length})
                </Typography>
                {sideVarNames.length > 0 && (
                  <IconButton
                    onClick={() => setSideVarNames([])}
                    size="small"
                    sx={{ color: 'text.secondary' }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto', px: 4, py: 2 }}>
              {sideVarNames.length === 0 ? (
                <Typography color="text.secondary" variant="body2" sx={{ py: 2, textAlign: 'center' }}>
                  Check variables in the tree, then click + to add them. One table will be created per variable.
                </Typography>
              ) : (
                <List dense disablePadding>
                  {sideVarNames.map(name => (
                    <ListItem key={name} disableGutters sx={{ py: 0.5 }}>
                      <ListItemText
                        primary={`${batch.generateTableName(name)}.cbt`}
                        secondary={`Side: ${name}`}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                      <ListItemSecondaryAction>
                        <IconButton edge="end" size="small" onClick={() => handleRemoveSideVariable(name)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </Box>

          <Divider orientation="vertical" flexItem />

          {/* Right panel: Top Axis Editor */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <AxisEditor
              title="Top Axis Editor"
              nodes={batch.topNodes}
              syntax={batch.topSyntax}
              onChange={batch.handleTopAxisChange}
              onSyntaxApply={batch.handleTopSyntaxApply}
              onAddSelected={() => {
                batch.handleAddSelectedToTopAxis(selectedVarNodes)
                variableTree.resetChecked()
              }}
              onOpenFilterModal={batch.handleOpenFilterModal}
              hasSelectedVariables={variableTree.checked.length > 0}
              error={batch.topAxisError}
            />
          </Box>
        </Box>
      </Box>

      {/* Filter Editor Modal */}
      <FilterEditorModal
        open={batch.filterModalOpen}
        onClose={batch.handleCloseFilterModal}
        currentFilter={
          batch.filterTargetNode?.type === 'Filter'
            ? batch.filterTargetNode.value1 || ''
            : batch.filterTargetNode
            ? ''
            : batch.filter
        }
        onSave={(newFilter) => {
          batch.handleSaveFilter(newFilter)
          batch.handleCloseFilterModal()
        }}
        customer={customer}
        job={job}
      />

      {/* Confirmation Dialog */}
      <BatchConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmGenerate}
        tables={tablePreviews}
        top={batch.topSyntax}
        filter={batch.filter}
        weight={batch.weight}
        caseFilter={batch.caseFilter}
        destinationFolder={displayDestination}
        loading={batch.loading}
      />

      {/* Folder Picker Dialog */}
      <FolderPickerDialog
        open={folderPickerOpen}
        onClose={() => setFolderPickerOpen(false)}
        onSelect={handleFolderSelect}
        tocNodes={account.toc || []}
        userEmail={account.user?.session_info?.email || ''}
        isManager={isManager}
      />
    </SideBarPage>
  )
}

export default BatchGenerate
